import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Plus, Trash2 } from "lucide-react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { getMyAvailability, saveMyAvailability } from "@/lib/booking.functions";
import { WEEKDAY_LABELS, minutesToLabel } from "@/lib/booking";
import { toast } from "sonner";

export const Route = createFileRoute("/agent/availability")({
  head: () => ({ meta: [{ title: "Availability — Currency Thoughts" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV}>
        <AvailabilityPage />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Win {
  weekday: number;
  start_minute: number;
  end_minute: number;
  slot_minutes: number;
  timezone: string;
}

const TIMES = Array.from({ length: 48 }, (_, i) => i * 30);

function AvailabilityPage() {
  const load = useServerFn(getMyAvailability);
  const save = useServerFn(saveMyAvailability);
  const browserTz = useMemo(
    () => (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC"),
    [],
  );
  const [rows, setRows] = useState<Win[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      const data = await load();
      setRows(
        (data ?? []).map((r) => ({
          weekday: r.weekday,
          start_minute: r.start_minute,
          end_minute: r.end_minute,
          slot_minutes: r.slot_minutes,
          timezone: r.timezone,
        })),
      );
    })();
  }, []);

  function update(i: number, patch: Partial<Win>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function onSave() {
    for (const r of rows) {
      if (r.end_minute <= r.start_minute) {
        toast.error(`${WEEKDAY_LABELS[r.weekday]}: end time must be after start time.`);
        return;
      }
    }
    setSaving(true);
    const res = await save({ data: { windows: rows } });
    setSaving(false);
    if (res.ok) toast.success("Availability published.");
    else toast.error(res.message ?? "Could not save.");
  }

  return (
    <PortalPage
      eyebrow="Scheduling"
      title="Your Availability"
      intro="Families book directly into these windows. Times are in the timezone you select; every booking is stored in UTC."
      actions={<PrimaryButton onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Publish availability"}</PrimaryButton>}
    >
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 rounded-[4px] border border-bronze-600/20 bg-white p-4 shadow-soft">
            <select
              className="rounded-[3px] border border-bronze-600/30 bg-cream-50 px-3 py-2 font-sans text-[13px] text-navy-800"
              value={r.weekday}
              onChange={(e) => update(i, { weekday: Number(e.target.value) })}
            >
              {WEEKDAY_LABELS.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
            </select>
            <select
              className="rounded-[3px] border border-bronze-600/30 bg-cream-50 px-3 py-2 font-sans text-[13px] text-navy-800"
              value={r.start_minute}
              onChange={(e) => update(i, { start_minute: Number(e.target.value) })}
            >
              {TIMES.map((t) => <option key={t} value={t}>{minutesToLabel(t)}</option>)}
            </select>
            <span className="font-sans text-[13px] text-charcoal-500">to</span>
            <select
              className="rounded-[3px] border border-bronze-600/30 bg-cream-50 px-3 py-2 font-sans text-[13px] text-navy-800"
              value={r.end_minute}
              onChange={(e) => update(i, { end_minute: Number(e.target.value) })}
            >
              {TIMES.slice(1).concat([1440]).map((t) => <option key={t} value={t}>{minutesToLabel(t % 1440)}</option>)}
            </select>
            <select
              className="rounded-[3px] border border-bronze-600/30 bg-cream-50 px-3 py-2 font-sans text-[13px] text-navy-800"
              value={r.slot_minutes}
              onChange={(e) => update(i, { slot_minutes: Number(e.target.value) })}
            >
              {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min slots</option>)}
            </select>
            <input
              className="min-w-[170px] flex-1 rounded-[3px] border border-bronze-600/30 bg-cream-50 px-3 py-2 font-sans text-[13px] text-navy-800"
              value={r.timezone}
              onChange={(e) => update(i, { timezone: e.target.value })}
              aria-label="Timezone"
            />
            <button
              type="button"
              onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
              className="text-charcoal-500 hover:text-danger"
              aria-label="Remove window"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="font-sans text-[13px] text-charcoal-500">
            No windows published yet — families can't book until you add one.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() =>
          setRows((prev) => [...prev, { weekday: 1, start_minute: 540, end_minute: 1020, slot_minutes: 30, timezone: browserTz }])
        }
        className="mt-5 flex items-center gap-2 font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600"
      >
        <Plus className="h-4 w-4" /> Add window
      </button>
    </PortalPage>
  );
}
